// @flow
import React, { useCallback } from 'react';
import type { Node } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useI18n } from '@shopify/react-i18n';
import _ from 'lodash';

import { Badge, Icon, Link } from '@shopify/polaris';
import * as AppIntegrationActions from 'actions/app-integration-actions';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { ExternalMinor } from '@shopify/polaris-icons';
import { getCurrentAppIntegration, isAppIntegrated } from 'src/helpers/integration-helper';

// One entry of WISHLIST in src/constants/integration
type TWishlistAppData = {
  name: string,
  icon: string,
  description: string,
  appListingURL: string,
  instructionURL: string,
  appType: string,
  appName: string,
  autoIntegration?: boolean
};

type TProps = {
  index: number,
  data: TWishlistAppData
};

const IntegrationWishlistCard = ({ index, data }: TProps): Node => {
  const [i18n] = useI18n();
  const dispatch = useDispatch();

  const tenantApp = useSelector(state => _.get(state.oauth, 'tenantApp', {}));
  const currentAppIntegration = getCurrentAppIntegration(tenantApp);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

  const {
    icon,
    name,
    appName,
    appType,
    appListingURL,
    description,
    instructionURL,
    autoIntegration = false
  } = data || {};

  const isIntegrated = isAppIntegrated(tenantApp, appType, appName);

  const handleToggle = useCallback(() => {
    dispatch(AppIntegrationActions.handleChangeApp(currentAppIntegration, appType, appName));
  }, [dispatch, currentAppIntegration, appType, appName]);

  const renderToggle = () => {
    if (!autoIntegration) return null;

    return (
      <label className="BC-Checkbox-Status">
        <input type="checkbox" checked={isIntegrated} onChange={handleToggle} />
        <span />
      </label>
    );
  };

  const renderIntegrationStatus = () => {
    if (!isEnableShopifyIntegration) return null;

    return (
      <div className="BC-Checkbox-Status__Wrapper">
        <div className="integration">
          <Badge status="info">
            <Link url={instructionURL} external>
              {i18n.translate('AdminHTML.Integration.Constants.integrationGuide')}
              <Icon source={ExternalMinor} />
            </Link>
          </Badge>
        </div>

        {renderToggle()}
      </div>
    );
  };

  return (
    <div className={index === 1 ? 'item middle' : 'item'} key={`item-${index}`}>
      <div className="item-header">
        <a className="Polaris-Link" href={appListingURL} target="_blank" rel="noopener noreferrer">
          <img className="icon" src={icon} alt={i18n.translate(name)} />
        </a>
      </div>
      {/* App name */}
      <div className="name">{i18n.translate(name)}</div>

      {/* App description */}
      <div className="description">{i18n.translate(description)}</div>

      {/* Enable/Disable app */}
      {renderIntegrationStatus()}
    </div>
  );
};

export default IntegrationWishlistCard;
